import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

export const CHART_COLORS = [
  "#22c55e", "#f97316", "#3b82f6", "#eab308", "#ec4899",
  "#14b8a6", "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16"
];

export const DonutChartCard = ({ title, icon: Icon, data, reportType, onViewClients, isDarkMode }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [_, count]) => sum + count, 0);
  const chartData = entries.map(([name, value], idx) => ({
    name: name.length > 20 ? name.substring(0, 20) + '...' : name,
    fullName: name,
    value,
    color: CHART_COLORS[idx % CHART_COLORS.length]
  }));

  const handleClick = (data) => {
    if (data && data.fullName) {
      onViewClients(reportType, data.fullName);
    }
  };

  return (
    <Card className={`border-0 shadow-lg rounded-2xl overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-white'}`} data-testid={`chart-${reportType}`}>
      <CardHeader className={`pb-2 transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg font-semibold flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#22c55e]/20' : 'bg-[#357c2a]/10'}`}>
              <Icon className="h-5 w-5 text-[#22c55e]" />
            </div>
            {title}
          </CardTitle>
          <Badge className="bg-[#22c55e] text-white px-3 py-1">{total} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {chartData.length > 0 ? (
          <div className="h-[300px] pt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="48%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                  stroke="none"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [value, props.payload.fullName]}
                  contentStyle={{
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid #22c55e' : 'none',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    backgroundColor: isDarkMode ? '#265324' : '#fff',
                    color: isDarkMode ? '#fff' : '#000',
                    padding: '12px 16px'
                  }}
                  itemStyle={{ color: isDarkMode ? '#fff' : '#000' }}
                  labelStyle={{ color: isDarkMode ? '#22c55e' : '#357c2a', fontWeight: 'bold' }}
                />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: '12px', paddingTop: '12px', color: isDarkMode ? '#fff' : '#64748b' }}
                  formatter={(value) => <span style={{ color: isDarkMode ? '#fff' : '#374151', fontWeight: '500' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center">
            <p className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const TableCardHalf = ({ title, icon: Icon, data, reportType, onViewClients, badgeColor = "bg-[#22c55e] text-white", isDarkMode }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [_, count]) => sum + count, 0);

  return (
    <Card className={`border-0 shadow-lg rounded-2xl overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-white'}`} data-testid={`table-${reportType}`}>
      <CardHeader className={`pb-2 transition-colors duration-300 ${isDarkMode ? 'bg-[#265324]' : 'bg-gradient-to-r from-slate-50 to-white'}`}>
        <div className="flex items-center justify-between">
          <CardTitle className={`text-lg font-semibold flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-[#22c55e]/20' : 'bg-[#357c2a]/10'}`}>
              <Icon className="h-5 w-5 text-[#22c55e]" />
            </div>
            {title}
          </CardTitle>
          <Badge className={`${badgeColor} px-3 py-1`}>{total} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {entries.length > 0 ? (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {entries.sort((a, b) => b[1] - a[1]).map(([name, count]) => (
              <div
                key={name}
                className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20' : 'hover:bg-slate-50'}`}
                onClick={() => onViewClients(reportType, name)}
                data-testid={`table-row-${reportType}-${name}`}
              >
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{name}</span>
                <Badge variant="outline" className={isDarkMode ? 'text-slate-300 border-slate-600' : ''}>{count}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
